import { Head, Link } from '@inertiajs/react'
import { useState } from 'react'
import { MarketingLayout } from '@/layouts/MarketingLayout'
import { ArrowRight, X } from 'lucide-react'

interface Post {
  id: number
  title: string
  slug: string
  excerpt: string | null
  author_name: string | null
  cover_image_url: string | null
  published_at: string
  reading_time_min: number | null
  tags: Array<{ name: string; slug: string }>
}

interface Props {
  posts: Post[]
  tags: Array<{ name: string; slug: string; post_count: number }>
  currentTag: string | null
  meta: { current_page: number; total_pages: number; total_count: number }
  seo: { title: string; description: string; canonical_url: string }
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

function compactDate(dateStr: string) {
  const date = new Date(dateStr)
  return {
    day: new Intl.DateTimeFormat('es-MX', { day: '2-digit', month: 'short' }).format(date),
    year: date.getFullYear(),
  }
}

function paginationUrl(page: number, currentTag: string | null) {
  const path = currentTag ? `/blog/tag/${currentTag}` : '/blog'
  return page === 1 ? path : `${path}?page=${page}`
}

export default function Index({ posts, tags, currentTag, meta, seo }: Props) {
  const [isNavigating, setIsNavigating] = useState(false)
  const navigationProps = {
    onStart: () => setIsNavigating(true),
    onFinish: () => setIsNavigating(false),
  }

  return (
    <MarketingLayout appUrl="">
      <Head title={seo.title}>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={seo.canonical_url} />
      </Head>

      <main className="mx-auto max-w-6xl px-4 pb-16 pt-28 sm:px-6 lg:px-8 lg:pb-24 lg:pt-36">
        <header className="mb-10 border-b border-border pb-10 lg:mb-12 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(18rem,30rem)] lg:items-end lg:gap-12">
          <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl">Blog</h1>
          <p className="mt-4 max-w-[65ch] text-base leading-7 text-muted-foreground lg:mt-0">
            Guías para atender mejor, reducir ausencias y llenar tu agenda desde WhatsApp.
          </p>
        </header>

        {tags.length > 0 && (
          <nav aria-label="Filtrar artículos por tema" className="mb-10 flex flex-wrap items-center gap-2">
            <Link
              href="/blog"
              {...navigationProps}
              className={`rounded-xl px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 active:bg-[#b9ff4a] active:text-black ${
                !currentTag
                  ? 'bg-[#b9ff4a] text-black'
                  : 'bg-secondary text-secondary-foreground hover:bg-foreground hover:text-background'
              }`}
            >
              Todos
            </Link>
            {tags.map((tag) => (
              <Link
                key={tag.slug}
                href={`/blog/tag/${tag.slug}`}
                {...navigationProps}
                className={`rounded-xl px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 active:bg-[#b9ff4a] active:text-black ${
                  currentTag === tag.slug
                    ? 'bg-[#b9ff4a] text-black'
                    : 'bg-secondary text-secondary-foreground hover:bg-foreground hover:text-background'
                }`}
              >
                {tag.name}
                <span className="ml-1.5 opacity-70">({tag.post_count})</span>
              </Link>
            ))}

            {currentTag && (
              <Link
                href="/blog"
                {...navigationProps}
                className="inline-flex min-h-10 items-center gap-1 rounded-xl px-3 text-sm font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2"
              >
                <X className="h-3 w-3" /> Limpiar filtro
              </Link>
            )}
          </nav>
        )}

        {posts.length > 0 ? (
          <section
            aria-busy={isNavigating}
            aria-label="Artículos"
            className={`border-y border-border transition-opacity ${isNavigating ? 'opacity-55' : 'opacity-100'}`}
          >
            <div className="hidden grid-cols-[4rem_minmax(0,1fr)_13rem_7rem] gap-6 border-b border-border py-3 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground lg:grid">
              <span>Fecha</span>
              <span>Artículo</span>
              <span>Temas</span>
              <span className="text-right">Lectura</span>
            </div>

            {posts.map((post, index) => {
              const date = compactDate(post.published_at)

              return (
                <Link
                  key={post.id}
                  href={`/blog/${post.slug}`}
                  {...navigationProps}
                  className="group grid gap-5 border-b border-border px-1 py-7 transition-colors last:border-b-0 hover:bg-secondary/55 focus-visible:rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 active:bg-[#b9ff4a]/35 lg:grid-cols-[4rem_minmax(0,1fr)_13rem_7rem] lg:items-center lg:gap-6 lg:px-0"
                >
                  <div className="flex items-baseline justify-between gap-4 lg:block">
                    <span className="text-xs font-semibold tabular-nums text-muted-foreground">
                      {String((meta.current_page - 1) * 12 + index + 1).padStart(2, '0')}
                    </span>
                    <time className="text-sm font-semibold capitalize text-foreground" dateTime={post.published_at}>
                      <span className="lg:block">{date.day}</span>{' '}
                      <span className="text-muted-foreground lg:block">{date.year}</span>
                    </time>
                  </div>

                  <div className={`grid min-w-0 gap-5 ${post.cover_image_url ? 'sm:grid-cols-[9rem_minmax(0,1fr)] sm:items-center' : ''}`}>
                    {post.cover_image_url && (
                      <img
                        src={post.cover_image_url}
                        alt=""
                        className="aspect-[4/3] w-full rounded-xl object-cover sm:w-36"
                        loading="lazy"
                      />
                    )}
                    <div className="min-w-0">
                      <h2 className="max-w-[30ch] text-xl font-bold leading-snug text-foreground decoration-2 underline-offset-4 group-hover:underline sm:text-2xl">
                        {post.title}
                      </h2>
                      {post.excerpt && (
                        <p className="mt-3 max-w-[70ch] text-sm leading-6 text-muted-foreground line-clamp-2">
                          {post.excerpt}
                        </p>
                      )}
                      <p className="mt-3 text-xs font-medium text-muted-foreground lg:hidden">
                        {post.reading_time_min ? `${post.reading_time_min} min de lectura` : formatDate(post.published_at)}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-x-3 gap-y-2" aria-label="Temas">
                    {post.tags.slice(0, 3).map((tag) => (
                      <span key={tag.slug} className="text-xs font-semibold text-muted-foreground">
                        {tag.name}
                      </span>
                    ))}
                  </div>

                  <div className="hidden items-center justify-end gap-3 text-sm font-semibold text-foreground lg:flex">
                    <span>{post.reading_time_min ? `${post.reading_time_min} min` : 'Leer'}</span>
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                  </div>
                </Link>
              )
            })}
          </section>
        ) : (
          <section className="rounded-xl border border-border px-6 py-16 text-center">
            <h2 className="text-xl font-bold text-foreground">No encontramos artículos</h2>
            <p className="mt-2 text-muted-foreground">Prueba otro tema o revisa todos los artículos.</p>
            {currentTag && (
              <Link
                href="/blog"
                {...navigationProps}
                className="mt-6 inline-flex min-h-11 items-center rounded-xl bg-foreground px-5 text-sm font-semibold text-background hover:bg-foreground/85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 active:bg-[#b9ff4a] active:text-black"
              >
                Ver todos
              </Link>
            )}
          </section>
        )}

        {meta.total_pages > 1 && (
          <nav aria-label="Paginación" className="mt-12 flex items-center justify-between gap-4 border-t border-border pt-6">
            {meta.current_page > 1 && (
              <Link
                href={paginationUrl(meta.current_page - 1, currentTag)}
                {...navigationProps}
                className="rounded-xl border border-border px-4 py-2 text-sm font-semibold hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 active:bg-[#b9ff4a] active:text-black"
              >
                Anterior
              </Link>
            )}
            <span className="mx-auto text-sm text-muted-foreground">
              Página {meta.current_page} de {meta.total_pages}
            </span>
            {meta.current_page < meta.total_pages && (
              <Link
                href={paginationUrl(meta.current_page + 1, currentTag)}
                {...navigationProps}
                className="rounded-xl border border-border px-4 py-2 text-sm font-semibold hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 active:bg-[#b9ff4a] active:text-black"
              >
                Siguiente
              </Link>
            )}
          </nav>
        )}
      </main>
    </MarketingLayout>
  )
}
